import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { createGlobalStyle, keyframes, css, ThemeProvider } from 'styled-components';

// Type-safe global assignments for MFE modules
(global as Record<string, unknown>).React = React;
(global as Record<string, unknown>).ReactDOM = ReactDOM;
(global as Record<string, unknown>).styled = styled;
((global as Record<string, unknown>).styled as Record<string, unknown>).createGlobalStyle =
  createGlobalStyle;
((global as Record<string, unknown>).styled as Record<string, unknown>).keyframes = keyframes;
((global as Record<string, unknown>).styled as Record<string, unknown>).css = css;
((global as Record<string, unknown>).styled as Record<string, unknown>).ThemeProvider =
  ThemeProvider;

// Type definitions for Network Information API
interface NetworkInformation {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  downlink?: number;
  rtt?: number;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

interface NetworkMetrics {
  online: boolean;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

type WindowWithModule = Window & Record<string, unknown>;

// Helper to get network connection quality metrics
const getNetworkMetrics = (): NetworkMetrics => {
  const nav = navigator as NavigatorWithConnection;
  const connection = nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
  return {
    online: navigator.onLine,
    effectiveType: connection?.effectiveType,
    downlink: connection?.downlink,
    rtt: connection?.rtt
  };
};

const useMfeModule = (
  assetManifestUrl: string,
  fileName: string,
  moduleName: string,
  DD_ACTION: string
) => {
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    if (isReady) return;

    const startTime = Date.now();
    let loadCompleted = false;

    datadogRum.addAction(DD_ACTION, {
      type: 'start',
      timestamp: startTime,
      manifestUrl: assetManifestUrl,
      moduleName
    });

    let node: HTMLScriptElement | null = null;
    let intervalId: NodeJS.Timeout | null = null;
    // Without this, a cleanup that ran mid-fetch (StrictMode's remount, or a real unmount) left the
    // fetch to append a second copy of the bundle and start a poll nothing ever cleared.
    const abortController = new AbortController();

    axios
      .get<Record<string, string>>(assetManifestUrl, { signal: abortController.signal })
      .then(assetManifest => {
        if (abortController.signal.aborted) return;
        const mainFile = assetManifest.data[fileName];
        datadogRum.addAction(`${DD_ACTION}-manifest`, { mainFile });
        node = document.createElement('script');
        node.src = mainFile;
        node.async = true;
        node.onerror = () => {
          loadCompleted = true;
          const loadTime = Date.now() - startTime;
          const networkMetrics = getNetworkMetrics();

          datadogRum.addAction(DD_ACTION, {
            type: 'error',
            errorType: 'script-load-failed',
            src: mainFile,
            timestamp: Date.now(),
            loadTime,
            ...networkMetrics
          });

          if (intervalId !== null) {
            clearInterval(intervalId);
          }
        };
        document.body.appendChild(node);
        intervalId = setInterval(() => {
          const downloadedMfeModule = (window as unknown as WindowWithModule)[moduleName];
          if (downloadedMfeModule) {
            loadCompleted = true;
            const loadTime = Date.now() - startTime;

            datadogRum.addAction(DD_ACTION, {
              type: 'success',
              timestamp: Date.now(),
              loadTime
            });
            setIsReady(true);
            if (intervalId !== null) {
              clearInterval(intervalId);
            }
          }
        }, 100);
      })
      .catch((e: unknown) => {
        if (axios.isCancel(e)) return;
        loadCompleted = true;
        const loadTime = Date.now() - startTime;
        const networkMetrics = getNetworkMetrics();

        // Extract HTTP status from axios error
        const error = axios.isAxiosError(e) ? e : null;
        const httpStatus = error?.response?.status;
        const httpStatusText = error?.response?.statusText;
        const errorMessage = error?.message ?? (e instanceof Error ? e.message : 'Unknown error');

        datadogRum.addAction(DD_ACTION, {
          type: 'error',
          errorType: 'manifest-fetch-failed',
          error: errorMessage,
          file: fileName,
          httpStatus,
          httpStatusText,
          timestamp: Date.now(),
          loadTime,
          ...networkMetrics
        });
      });

    return () => {
      abortController.abort();
      // Track abandoned loads when component unmounts before completion
      if (!loadCompleted) {
        const timeElapsed = Date.now() - startTime;
        datadogRum.addAction(DD_ACTION, {
          type: 'abandoned',
          reason: 'component-unmounted',
          timeElapsed,
          timestamp: Date.now()
        });
      }

      if (node != null) {
        document.body.removeChild(node);
      }
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [assetManifestUrl, fileName, moduleName, isReady, DD_ACTION]);
  return isReady;
};

export default useMfeModule;
